import zod from 'zod';
import { TuyaIntegrationFormSchema } from '../../../services/IntegrationService/TuyaService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useCreateTuyaIntegrationAsync,
  useGetTuyaIntegrationByIdAsync,
  useUpdateTuyaIntegrationAsync,
} from '../../../services/IntegrationService/TuyaService/hooks/useMutation';
import { Box, Grid2 } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import TooltipComponent from '../../../components/TooltipComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { t } from 'i18next';

interface ITuyaEditionFormProps {
  tuyaIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type TuyaEditionFormInput = zod.infer<typeof TuyaIntegrationFormSchema>;

const TuyaEditionForm = ({
  tuyaIdToUpdate,
  handleSaveForm,
}: ITuyaEditionFormProps) => {
  const { control, reset, handleSubmit } = useForm<TuyaEditionFormInput>({
    resolver: zodResolver(TuyaIntegrationFormSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      tuyaUserId: '',
    },
  });
  const { mutateAsync: mutateGet } = useGetTuyaIntegrationByIdAsync();
  const { mutateAsync: mutateCreate } = useCreateTuyaIntegrationAsync();
  const { mutateAsync: mutateUpdate } = useUpdateTuyaIntegrationAsync();
  const { showSnackbar } = useContext(AppContext);

  const handleSave = async (data: TuyaEditionFormInput) => {
    if (tuyaIdToUpdate) {
      await mutateUpdate({ id: tuyaIdToUpdate, data }).then(() => {
        saveIntegration('Integração tuya atualizada com sucesso.');
      });
    } else {
      await mutateCreate(data).then(() => {
        saveIntegration('Integração defense criada com sucesso.');
      });
    }
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (tuyaIdToUpdate) {
      mutateGet(tuyaIdToUpdate).then((i) =>
        reset(i, { keepDefaultValues: true }),
      );
    }
  }, [tuyaIdToUpdate]);

  return (
    <>
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.tuyaForms.name')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.tuyaForms.description')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  multiline
                  maxRows={4}
                />
              )}
              name={'description'}
              control={control}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.tuyaForms.idUserTuya')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'tuyaUserId'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Box width={'14%'}>
                  <TooltipComponent
                    title={value ? 'Ativo' : 'Inativo'}
                    placement={'right'}
                  >
                    <SwitchComponent
                      label={t('integrationsPage.tuyaForms.status')}
                      value={value}
                      onChange={(value) => onChange(value)}
                      color={'primary'}
                    />
                  </TooltipComponent>
                </Box>
              )}
              name={'isActive'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <ButtonComponent
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit(handleSave)}
        >
          {t('integrationsPage.tuyaForms.save')}
        </ButtonComponent>
      </Grid2>
    </>
  );
};

export default TuyaEditionForm;
